import type { SessionCodePlayerCount } from "./types";

/**
 * Short, offline, human-typeable room code.
 *
 * Unlike the self-contained `YSI1.` session code, a room code carries only the
 * pieces of configuration that actually vary between v1 games — the player
 * count, the chosen theme, and a short random seed — and lets every device
 * reconstruct the full deal configuration from fixed v1 constants. It stays
 * fully offline: no server maps the code back to a config. The whole normalized
 * code string is what the deal uses as its raw seed, so create and join derive
 * identical deals; the theme index makes joiners land on the host's theme.
 *
 * Layout: 6 Crockford-base32 characters. The first 5 encode a 25-bit payload
 * (2 high bits = player index, next 3 bits = theme index, low 20 bits = seed);
 * the 6th is a checksum for catching manual-entry typos before the code is
 * trusted.
 */

// Crockford base32 without I, L, O, U — avoids characters players confuse.
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const PAYLOAD_CHARS = 5;
export const ROOM_CODE_LENGTH = 6;

const PLAYER_COUNTS: readonly SessionCodePlayerCount[] = [3, 4, 5];
const SEED_BITS = 20;
const THEME_BITS = 3;
export const MAX_ROOM_SEED = 1 << SEED_BITS; // 2^20 exclusive upper bound
export const MAX_ROOM_THEME_INDEX = 1 << THEME_BITS; // 8 themes addressable

export type RoomCodeErrorCode =
  | "INVALID_LENGTH"
  | "INVALID_CHARACTER"
  | "CHECKSUM_MISMATCH"
  | "INVALID_PLAYER_COUNT"
  | "INVALID_THEME_INDEX"
  | "INVALID_SEED";

export class RoomCodeError extends Error {
  readonly code: RoomCodeErrorCode;

  constructor(code: RoomCodeErrorCode, message: string) {
    super(message);
    this.name = "RoomCodeError";
    this.code = code;
  }
}

export interface RoomCodeParts {
  readonly playerCount: SessionCodePlayerCount;
  readonly themeIndex: number;
  readonly seed: number;
}

export interface ParsedRoomCode extends RoomCodeParts {
  /** Canonical uppercase form, safe to use as a deal seed and to display. */
  readonly code: string;
}

function fail(code: RoomCodeErrorCode, message: string): never {
  throw new RoomCodeError(code, message);
}

/** Position-sensitive checksum over the payload symbol values (mod 32). */
function checkSymbol(values: readonly number[]): number {
  let accumulator = 0;
  for (const value of values) {
    accumulator = (accumulator * 31 + value + 1) % 1021;
  }
  return accumulator % ALPHABET.length;
}

function encodeBase32(value: number, length: number): string {
  let remaining = value;
  let result = "";
  for (let index = 0; index < length; index += 1) {
    result = ALPHABET[remaining % ALPHABET.length] + result;
    remaining = Math.floor(remaining / ALPHABET.length);
  }
  return result;
}

/** A cryptographically-random 23-bit seed. Honest-mode entropy is plenty. */
export function randomRoomSeed(): number {
  const [word] = crypto.getRandomValues(new Uint32Array(1));
  return (word ?? 0) % MAX_ROOM_SEED;
}

export function formatRoomCode({
  playerCount,
  themeIndex,
  seed
}: RoomCodeParts): string {
  const playerIndex = PLAYER_COUNTS.indexOf(playerCount);
  if (playerIndex < 0) {
    fail("INVALID_PLAYER_COUNT", "Room code player count must be 3, 4, or 5");
  }
  if (!Number.isInteger(themeIndex) || themeIndex < 0 || themeIndex >= MAX_ROOM_THEME_INDEX) {
    fail("INVALID_THEME_INDEX", `Room code theme index must be an integer in 0..${MAX_ROOM_THEME_INDEX - 1}`);
  }
  if (!Number.isInteger(seed) || seed < 0 || seed >= MAX_ROOM_SEED) {
    fail("INVALID_SEED", `Room code seed must be an integer in 0..${MAX_ROOM_SEED - 1}`);
  }
  const payloadValue =
    (playerIndex << (THEME_BITS + SEED_BITS)) | (themeIndex << SEED_BITS) | seed;
  const payload = encodeBase32(payloadValue, PAYLOAD_CHARS);
  const values = [...payload].map((character) => ALPHABET.indexOf(character));
  return payload + ALPHABET[checkSymbol(values)];
}

/** Uppercase, drop separators, and fold characters players commonly mistype. */
export function normalizeRoomCode(input: string): string {
  return input
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/gu, "")
    .replaceAll("I", "1")
    .replaceAll("L", "1")
    .replaceAll("O", "0")
    .replaceAll("U", "V");
}

export function parseRoomCode(input: string): ParsedRoomCode {
  const code = normalizeRoomCode(input);
  if (code.length !== ROOM_CODE_LENGTH) {
    fail("INVALID_LENGTH", `Room code must be ${ROOM_CODE_LENGTH} characters`);
  }
  const values = [...code].map((character) => ALPHABET.indexOf(character));
  if (values.some((value) => value < 0)) {
    fail("INVALID_CHARACTER", "Room code contains an unsupported character");
  }
  const payloadValues = values.slice(0, PAYLOAD_CHARS);
  const expectedCheck = ALPHABET[checkSymbol(payloadValues)];
  if (code[PAYLOAD_CHARS] !== expectedCheck) {
    fail("CHECKSUM_MISMATCH", "Room code checksum does not match");
  }
  const payloadValue = payloadValues.reduce(
    (accumulator, value) => accumulator * ALPHABET.length + value,
    0
  );
  const playerIndex = payloadValue >> (THEME_BITS + SEED_BITS);
  const playerCount = PLAYER_COUNTS[playerIndex];
  if (playerCount === undefined) {
    fail("INVALID_PLAYER_COUNT", "Room code encodes an unsupported player count");
  }
  return {
    code,
    playerCount,
    themeIndex: (payloadValue >> SEED_BITS) & (MAX_ROOM_THEME_INDEX - 1),
    seed: payloadValue & (MAX_ROOM_SEED - 1)
  };
}

export function roomCodeFragment(code: string): string {
  return `#ysi=${encodeURIComponent(code)}`;
}

export function roomCodeFromFragment(fragment: string): string | null {
  const raw = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  const code = new URLSearchParams(raw).get("ysi");
  return code === null || code.length === 0 ? null : code;
}
